/**
 * Client workspace · Overview tab — Task 044 setup-mode + Task 162
 * foundation-relocation cleanup.
 *
 * Read: `get-client-overview` + `list-client-initiatives`.
 * Write: `update-client`, `upsert-client-brand-info`,
 *        `create-client-initiative`.
 *
 * Layout when client is in `onboarding`: top onboarding panel with a
 * data-driven checklist (brand / audience / goals / initiatives) and an
 * "إنهاء الإعداد وتفعيل العميل" CTA. Audience and goals are managed in
 * the Onboarding wizard (Task 162 — `/app/clients/:clientId/onboarding`);
 * the checklist links cross-page for those rows. Brand identity and
 * initiatives are still edited on this page.
 *
 * UX-only permission gates (the load-bearing guards are RLS):
 *   - Edit/setup controls require `client.manage` or `client.self.manage`.
 *   - Initiative creation additionally requires `initiative.create`.
 *
 * Refresh strategy: every successful write triggers a full overview
 * reload via `setReloadKey` so derived state (checklist progress,
 * activate-button enablement) stays in sync without optimistic-merge
 * complexity.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { isApiError, KmiApiError } from '../../api/client';
import type { ClientWorkspaceOutletContext } from '../ClientWorkspaceLayout';
import { capabilityLabelAr } from '../../permissions';
import {
  createClientInitiative,
  getClientOnboardingProfile,
  getClientOverview,
  listClientInitiatives,
  updateClient,
  upsertClientBrandInfo,
} from '../../api/endpoints';
import type {
  BrandInfo,
  ClientGoal,
  ClientOnboardingProfile,
  ClientOverviewData,
  ClientStatus,
  InitiativeListRow,
  InitiativeStatus,
  TargetAudience,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import { hasAnyPermission, PERM } from '../../permissions';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';
import { OperatingSetupSection } from './OperatingSetupSection';

type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly data: ClientOverviewData;
      readonly initiatives: ReadonlyArray<InitiativeListRow>;
      readonly onboardingProfile: ClientOnboardingProfile | null;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

export function OverviewPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const workspace = useOutletContext<ClientWorkspaceOutletContext>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  const canManage =
    !!me &&
    !me.system_admin &&
    hasAnyPermission(me.permissions, PERM.CLIENT_MANAGE, PERM.CLIENT_SELF_MANAGE);
  // Task 279 — operating setup is AGENCY-ONLY to edit (mirrors the EF/RLS write
  // gate: client.manage, NOT client.self.manage). Client-category users read-only.
  const canManageOperatingSetup =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.CLIENT_MANAGE);
  const canCreateInitiative =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.INITIATIVE_CREATE);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    Promise.all([
      getClientOverview(clientId, { signal: ctrl.signal }),
      // Initiatives: tolerate a soft-fail. The actor may have
      // `client.manage` but not `initiative.read` — RLS will surface as
      // an empty list, which is fine. Only collapse to error/not-found
      // for the overview itself.
      listClientInitiatives(clientId, { signal: ctrl.signal }).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (isApiError(err) && (err.code === 'not_found' || err.code === 'permission_denied')) {
          return { initiatives: [] as ReadonlyArray<InitiativeListRow> };
        }
        throw err;
      }),
      getClientOnboardingProfile(clientId, { signal: ctrl.signal }).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (isApiError(err) && (err.code === 'not_found' || err.code === 'permission_denied')) {
          return { onboarding_profile: null };
        }
        throw err;
      }),
    ])
      .then(([overview, init, onboarding]) => {
        if (ctrl.signal.aborted) return;
        setState({
          status: 'ready',
          data: overview,
          initiatives: init.initiatives,
          onboardingProfile: onboarding.onboarding_profile,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setState({ status: 'not-found' });
          return;
        }
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  if (!clientId) return null;

  if (state.status === 'loading') return <LoadingState title="جاري تحميل نظرة العميل…" />;
  if (state.status === 'not-found')
    return (
      <NotFoundState
        title="لا يمكن الوصول إلى هذا العميل"
        description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح ملفه."
      />
    );
  if (state.status === 'error')
    return (
      <ErrorState
        title="تعذر تحميل نظرة العميل"
        error={state.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );

  const { client, brand_info: brand, target_audiences: audiences, goals } = state.data;
  const initiatives = state.initiatives;
  const onboardingProfile = state.onboardingProfile;
  const refresh = () => setReloadKey((n) => n + 1);
  const isOnboarding = client.status === 'onboarding';

  return (
    <div className="stack-md">
      {isOnboarding ? (
        <OnboardingPanel
          clientId={clientId}
          status={client.status}
          hasBrand={!!brand}
          hasAudience={audiences.length > 0}
          hasGoal={goals.length > 0}
          hasInitiative={initiatives.length > 0}
          canManage={canManage}
          onActivated={refresh}
        />
      ) : null}

      <PackageStateBanner workspace={workspace} clientId={clientId} />

      <BrandSection clientId={clientId} brand={brand} canManage={canManage} onSaved={refresh} />

      <OperatingSetupSection
        clientId={clientId}
        canEdit={canManageOperatingSetup}
        activeAssignment={workspace.activeAssignment}
        capabilities={workspace.capabilities}
      />

      <OnboardingSummaryCard
        clientId={clientId}
        profile={onboardingProfile}
        brand={brand}
        audiences={audiences}
        goals={goals}
        canManage={canManage}
      />

      <InitiativesSection
        clientId={clientId}
        initiatives={initiatives}
        canCreate={canCreateInitiative}
        onSaved={refresh}
      />
    </div>
  );
}

// ── Package state banner (Task 051) ───────────────────────────────────

function PackageStateBanner({
  workspace,
  clientId,
}: {
  readonly workspace: ClientWorkspaceOutletContext;
  readonly clientId: string;
}) {
  if (workspace.capabilityStatus === 'loading') return null;
  if (workspace.capabilityStatus === 'error') return null;

  if (!workspace.hasActivePackage) {
    return (
      <div className="card package-banner package-banner-empty">
        <div>
          <strong>لا توجد باقة مفعّلة</strong>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            تابة الأساسيات (نظرة عامة، التأهيل، الاستراتيجية، التقارير، الخدمات) متاحة دائماً. تابات
            التنفيذ (المحتوى، النشر، الإعلانات، الإبداع) ستظهر بعد تفعيل باقة تشمل القدرة المناسبة.
          </p>
        </div>
        <Link
          to={`/app/clients/${clientId}/services`}
          className="btn-text"
          style={{ padding: '8px 12px' }}
        >
          إدارة الباقات ←
        </Link>
      </div>
    );
  }

  const caps = Array.from(workspace.capabilities).filter((cap) => cap !== 'influencers');
  if (caps.length === 0) {
    return (
      <div className="card package-banner package-banner-empty">
        <strong>الباقة مفعّلة بدون قدرات تنفيذ</strong>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          الباقة الحالية لا تتضمّن أي قدرة تنفيذ (محتوى، نشر، إعلانات، إبداع). راجع الخدمات المضمّنة
          من تاب الخدمات.
        </p>
      </div>
    );
  }

  return (
    <div className="card package-banner package-banner-ready">
      <div>
        <span className="lbl">القدرات المفعّلة بالباقة الحالية</span>
        <div className="package-banner-chips">
          {caps.map((c) => (
            <span key={c} className="pill pill-active">
              {capabilityLabelAr(c)}
            </span>
          ))}
        </div>
      </div>
      <Link
        to={`/app/clients/${clientId}/services`}
        className="btn-text"
        style={{ padding: '8px 12px' }}
      >
        تفاصيل الباقة ←
      </Link>
    </div>
  );
}

// ── Onboarding checklist panel ───────────────────────────────────────

function OnboardingPanel({
  clientId,
  status,
  hasBrand,
  hasAudience,
  hasGoal,
  hasInitiative,
  canManage,
  onActivated,
}: {
  readonly clientId: string;
  readonly status: ClientStatus;
  readonly hasBrand: boolean;
  readonly hasAudience: boolean;
  readonly hasGoal: boolean;
  readonly hasInitiative: boolean;
  readonly canManage: boolean;
  readonly onActivated: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Task 162 — checklist anchors:
  //   - brand / initiatives still live on this page (in-page hash).
  //   - audience / goals live in the Onboarding wizard, so those rows
  //     link cross-page via `<Link to="/app/clients/.../onboarding#...">`.
  // The two onboarding-wizard ids match `sectionAnchorId(...)` in
  // `OnboardingWizardPage.tsx`: the audience editor lives in the
  // market step, and goals are their own step.
  const onboardingPath = `/app/clients/${clientId}/onboarding`;
  const items: ReadonlyArray<{
    label: string;
    done: boolean;
    key: string;
    href: string;
    crossPage: boolean;
  }> = [
    {
      label: 'معلومات العلامة',
      done: hasBrand,
      key: 'brand',
      href: '#brand-info-section',
      crossPage: false,
    },
    {
      label: 'الجمهور المستهدف',
      done: hasAudience,
      key: 'audience',
      href: `${onboardingPath}#onboarding-section-market`,
      crossPage: true,
    },
    {
      label: 'الأهداف',
      done: hasGoal,
      key: 'goals',
      href: `${onboardingPath}#onboarding-section-goals`,
      crossPage: true,
    },
    {
      label: 'المبادرات',
      done: hasInitiative,
      key: 'initiatives',
      href: '#initiatives-section',
      crossPage: false,
    },
  ];
  const minimumMet = hasBrand && hasAudience && hasGoal;
  const completedCount = items.filter((i) => i.done).length;

  async function activate() {
    setError(null);
    setSubmitting(true);
    try {
      await updateClient({ input: { client_id: clientId, status: 'active' } });
      onActivated();
    } catch (err) {
      setSubmitting(false);
      if (err instanceof KmiApiError) {
        if (err.code === 'permission_denied') {
          setError('لا تملك صلاحية تفعيل هذا العميل.');
          return;
        }
        if (err.code === 'validation_error') {
          // Backend mirrors the frontend's readiness gate: brand_info +
          // ≥1 audience + ≥1 goal must exist before activation. The
          // local UX gate normally blocks this; reaching here means the
          // local state was stale (e.g. racing two tabs). Surface a
          // specific message and ask the user to refresh.
          setError(
            'الإعداد غير مكتمل. يجب توفير هوية العلامة وجمهور واحد على الأقل وهدف واحد على الأقل قبل التفعيل.',
          );
          return;
        }
      }
      setError('تعذّر تفعيل العميل. حاول مجدداً.');
    }
  }

  return (
    <div
      className="card"
      style={{
        padding: 22,
        background: 'linear-gradient(135deg, var(--cream-2) 0%, var(--tangerine-soft) 100%)',
        border: '1px solid var(--tangerine)',
      }}
    >
      <div className="sec-title" style={{ marginBottom: 8 }}>
        <h2>إعداد العميل</h2>
        <span className="meta">
          {completedCount} / {items.length}
        </span>
      </div>
      <p className="body" style={{ marginBottom: 14, color: 'var(--ink-2)' }}>
        أكمل عناصر الإعداد التالية، ثم فعّل العميل لبدء العمل عليه. الحالة الحالية:{' '}
        <strong>{statusLabel(status)}</strong>.
      </p>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '0 0 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {items.map((it) => (
          <li
            key={it.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              background: 'var(--paper)',
              border: '1px solid var(--line-2)',
              borderRadius: 'var(--r-md)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                background: it.done ? 'var(--sage)' : 'var(--line)',
                color: it.done ? '#fff' : 'var(--muted)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {it.done ? '✓' : '·'}
            </span>
            <span style={{ flex: 1, fontWeight: 500 }}>{it.label}</span>
            {it.crossPage ? (
              <Link to={it.href} className="btn-text" style={{ padding: '4px 8px', fontSize: 12 }}>
                {it.done ? 'مراجعة' : 'إعداد'} →
              </Link>
            ) : (
              <a href={it.href} className="btn-text" style={{ padding: '4px 8px', fontSize: 12 }}>
                {it.done ? 'مراجعة' : 'إعداد'} ↓
              </a>
            )}
          </li>
        ))}
      </ul>

      {error ? (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="auth-cta"
          onClick={activate}
          disabled={!canManage || !minimumMet || submitting}
          title={
            !canManage
              ? 'لا تملك صلاحية client.manage'
              : !minimumMet
                ? 'أكمل العلامة + الجمهور + هدف واحد على الأقل'
                : undefined
          }
          style={{ width: 'auto' }}
        >
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري التفعيل…' : 'إنهاء الإعداد وتفعيل العميل'}</span>
          {!submitting ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          ) : null}
        </button>
        {!minimumMet && canManage ? (
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            الحد الأدنى: العلامة + جمهور واحد + هدف واحد. المبادرات اختيارية للتفعيل.
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ── Brand info section ───────────────────────────────────────────────

function BrandSection({
  clientId,
  brand,
  canManage,
  onSaved,
}: {
  readonly clientId: string;
  readonly brand: BrandInfo | null;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [editing, setEditing] = useState(!brand);
  // If the parent's `brand` flips from null → set (after save+refresh),
  // collapse back to read mode automatically.
  useEffect(() => {
    if (brand) setEditing(false);
  }, [brand]);

  return (
    <section id="brand-info-section" className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>هوية العلامة</h2>
        <span className="meta">brand_info</span>
      </div>
      {!editing && brand ? (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
              gap: 14,
            }}
          >
            <Field label="الاسم" value={brand.brand_name} />
            <Field label="القطاع" value={brand.industry} />
            <Field label="اللغة الأساسية" value={brand.primary_locale} />
            <Field label="الموقع" value={brand.website_url} />
          </div>
          {canManage ? (
            <div style={{ marginTop: 14 }}>
              <button type="button" className="btn-text" onClick={() => setEditing(true)}>
                تعديل
              </button>
            </div>
          ) : null}
        </div>
      ) : !brand && !canManage ? (
        <EmptyState
          title="لم يتم تسجيل هوية العلامة بعد"
          description="ستظهر هنا تفاصيل العلامة عندما يقوم Owner / مدير التسويق بتعبئتها."
        />
      ) : (
        <BrandForm
          clientId={clientId}
          initial={brand}
          canSubmit={canManage}
          onSaved={onSaved}
          onCancel={brand ? () => setEditing(false) : undefined}
        />
      )}
    </section>
  );
}

function BrandForm({
  clientId,
  initial,
  canSubmit,
  onSaved,
  onCancel,
}: {
  readonly clientId: string;
  readonly initial: BrandInfo | null;
  readonly canSubmit: boolean;
  readonly onSaved: () => void;
  readonly onCancel?: () => void;
}) {
  const [brandName, setBrandName] = useState(initial?.brand_name ?? '');
  const [industry, setIndustry] = useState(initial?.industry ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initial?.website_url ?? '');
  const [primaryLocale, setPrimaryLocale] = useState(initial?.primary_locale ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setFieldErrors({});
    const trimmed = brandName.trim();
    if (trimmed.length === 0) {
      setFieldErrors({ brand_name: ['اسم العلامة مطلوب.'] });
      return;
    }
    setSubmitting(true);
    try {
      await upsertClientBrandInfo({
        input: {
          client_id: clientId,
          brand_name: trimmed,
          industry: industry.trim() || null,
          website_url: websiteUrl.trim() || null,
          primary_locale: primaryLocale.trim() || null,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {error ? (
        <div className="banner banner-error" style={{ marginBottom: 14 }}>
          {error}
        </div>
      ) : null}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
          gap: 14,
        }}
      >
        <FormField
          id="brand_name"
          label="اسم العلامة"
          value={brandName}
          onChange={setBrandName}
          required
          maxLength={160}
          disabled={submitting || !canSubmit}
          error={fieldErrors['brand_name']?.[0]}
        />
        <FormField
          id="industry"
          label="القطاع"
          value={industry}
          onChange={setIndustry}
          maxLength={80}
          disabled={submitting || !canSubmit}
          placeholder="مثال: ضيافة"
          error={fieldErrors['industry']?.[0]}
        />
        <FormField
          id="website_url"
          label="الموقع"
          value={websiteUrl}
          onChange={setWebsiteUrl}
          maxLength={500}
          disabled={submitting || !canSubmit}
          placeholder="https://example.com"
          dirLtr
          error={fieldErrors['website_url']?.[0]}
        />
        <FormField
          id="primary_locale"
          label="اللغة الأساسية"
          value={primaryLocale}
          onChange={setPrimaryLocale}
          maxLength={16}
          disabled={submitting || !canSubmit}
          placeholder="ar-SA"
          dirLtr
          error={fieldErrors['primary_locale']?.[0]}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          type="submit"
          className="auth-cta"
          disabled={!canSubmit || submitting}
          style={{ width: 'auto' }}
        >
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الحفظ…' : 'حفظ هوية العلامة'}</span>
        </button>
        {onCancel ? (
          <button
            type="button"
            className="btn-text"
            onClick={onCancel}
            disabled={submitting}
            style={{ padding: '10px 14px' }}
          >
            إلغاء
          </button>
        ) : null}
      </div>
    </form>
  );
}

// ── Onboarding profile summary (Task 049 + Task 162) ─────────────────
// The deep editing experience lives at /onboarding (wizard). This card
// is the compact at-a-glance summary on Overview: completion %, top
// missing sections, and a CTA into the wizard. Read-only users see the
// same surface — they just don't get the edit CTA.
//
// Task 162 added a 6th `goals` chip because goals are now an
// onboarding-managed step (was previously a long editor on this page).
// The chip list mirrors the wizard's fill steps so the at-a-glance
// status stays accurate.

function OnboardingSummaryCard({
  clientId,
  profile,
  brand,
  audiences,
  goals,
  canManage,
}: {
  readonly clientId: string;
  readonly profile: ClientOnboardingProfile | null;
  readonly brand: BrandInfo | null;
  readonly audiences: ReadonlyArray<TargetAudience>;
  readonly goals: ReadonlyArray<ClientGoal>;
  readonly canManage: boolean;
}) {
  const sections: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly done: boolean;
    readonly step: string;
  }> = [
    {
      key: 'basics',
      label: 'الأساسيات',
      done: !!brand && !!brand.brand_name && brand.brand_name.trim().length > 0,
      step: 'basics',
    },
    {
      key: 'identity',
      label: 'الهوية والصوت',
      done:
        !!profile &&
        ((profile.vision ?? '').trim().length > 0 ||
          (profile.mission ?? '').trim().length > 0 ||
          (profile.values_text ?? '').trim().length > 0 ||
          (profile.usp ?? '').trim().length > 0 ||
          (profile.arabic_description ?? '').trim().length > 0 ||
          (profile.english_description ?? '').trim().length > 0),
      step: 'identity',
    },
    {
      key: 'market',
      label: 'السوق',
      done:
        audiences.length > 0 ||
        (!!profile &&
          ((profile.strengths ?? '').trim().length > 0 ||
            (profile.weaknesses ?? '').trim().length > 0 ||
            (profile.offers_notes ?? '').trim().length > 0)),
      step: 'market',
    },
    {
      key: 'products',
      label: 'المنتجات',
      done: !!profile && profile.products_services.length > 0,
      step: 'products',
    },
    {
      key: 'competitors',
      label: 'المنافسون',
      done: !!profile && profile.competitors.length > 0,
      step: 'competitors',
    },
    {
      key: 'goals',
      label: 'الأهداف',
      done: goals.length > 0,
      step: 'goals',
    },
  ];

  const doneCount = sections.filter((s) => s.done).length;
  const percent = Math.round((doneCount / sections.length) * 100);
  const missing = sections.filter((s) => !s.done);
  const completedAt = profile?.completed_at ?? null;
  const wizardLink = `/app/clients/${clientId}/onboarding`;

  return (
    <section id="onboarding-profile-section" className="card onboarding-summary-card">
      <div className="sec-title">
        <div>
          <h2>ملف التأهيل</h2>
          <p className="muted" style={{ margin: '6px 0 0' }}>
            الرؤية، الرسالة، السوق، المنتجات، والمنافسون. يُحرَّر من ويزرد التأهيل المخصّص.
          </p>
        </div>
        <span className="meta">client_onboarding_profile</span>
      </div>

      <div className="onboarding-summary-row">
        <div className="onboarding-summary-progress">
          <div
            className="onboarding-progress-bar"
            role="progressbar"
            aria-label="تقدم ملف التأهيل"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="onboarding-progress-bar-fill" style={{ width: `${percent}%` }} />
          </div>
          <strong style={{ fontSize: 18, marginInlineStart: 12 }}>
            {doneCount} / {sections.length}
          </strong>
        </div>
        <div className="onboarding-summary-status">
          <span
            className={
              completedAt
                ? 'pill pill-active'
                : doneCount === sections.length
                  ? 'pill pill-review'
                  : 'pill pill-paused'
            }
          >
            {completedAt
              ? 'مُقرّ بالاكتمال'
              : doneCount === sections.length
                ? 'كل الأقسام مكتملة'
                : 'قيد التأهيل'}
          </span>
          {completedAt ? (
            <span className="tiny" style={{ color: 'var(--muted)' }}>
              {new Date(completedAt).toLocaleDateString('ar-SA')}
            </span>
          ) : null}
        </div>
      </div>

      <div className="onboarding-summary-chips">
        {sections.map((s) => (
          <Link
            key={s.key}
            to={`${wizardLink}?step=${s.step}`}
            className={`onboarding-summary-chip${s.done ? ' is-done' : ''}`}
          >
            <span aria-hidden="true">{s.done ? '✓' : '·'}</span>
            <span>{s.label}</span>
          </Link>
        ))}
      </div>

      {missing.length > 0 ? (
        <p className="tiny" style={{ color: 'var(--muted)', margin: '4px 0 12px' }}>
          ناقص: {missing.map((m) => m.label).join('، ')}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Link to={wizardLink} className="auth-cta" style={{ width: 'auto' }}>
          <span>{canManage ? 'فتح ويزرد التأهيل' : 'عرض ملف التأهيل'}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
        {!canManage ? (
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            وضع القراءة. التحرير يحتاج صلاحية client.manage أو client.self.manage.
          </span>
        ) : null}
      </div>
    </section>
  );
}

// ── Initiatives section ──────────────────────────────────────────────

function InitiativesSection({
  clientId,
  initiatives,
  canCreate,
  onSaved,
}: {
  readonly clientId: string;
  readonly initiatives: ReadonlyArray<InitiativeListRow>;
  readonly canCreate: boolean;
  readonly onSaved: () => void;
}) {
  const [showForm, setShowForm] = useState(false);

  return (
    <section id="initiatives-section" className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>المبادرات</h2>
        <span className="meta">{initiatives.length}</span>
      </div>
      {initiatives.length === 0 ? (
        <EmptyState
          title="لا توجد مبادرات بعد"
          description="ابدأ بإضافة أول مبادرة لتتبع تقدّم العمل على هذا العميل."
        />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0 0 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {initiatives.map((i) => (
            <li
              key={i.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 12px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>{i.name}</div>
                <div className="tiny" style={{ color: 'var(--muted)', marginTop: 4 }}>
                  {i.start_date ? `من ${i.start_date}` : 'بدون تاريخ بدء'}
                  {i.target_end_date ? ` · إلى ${i.target_end_date}` : ''}
                </div>
              </div>
              <span className={`pill pill-${initiativeVariant(i.status)}`}>
                {initiativeLabel(i.status)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {canCreate ? (
        showForm ? (
          <InitiativeForm
            clientId={clientId}
            onSaved={() => {
              setShowForm(false);
              onSaved();
            }}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <button
            type="button"
            className="btn-text"
            onClick={() => setShowForm(true)}
            style={{ padding: '8px 12px' }}
          >
            + إضافة مبادرة
          </button>
        )
      ) : (
        <p className="tiny" style={{ marginTop: 10, color: 'var(--muted)' }}>
          إنشاء المبادرات يتطلّب صلاحية initiative.create. تعديل المبادرات والمعالم سيُفعّل في مهمة
          لاحقة.
        </p>
      )}
    </section>
  );
}

function InitiativeForm({
  clientId,
  onSaved,
  onCancel,
}: {
  readonly clientId: string;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetEndDate, setTargetEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setFieldErrors({ name: ['اسم المبادرة مطلوب.'] });
      return;
    }
    if (startDate && targetEndDate && targetEndDate < startDate) {
      setFieldErrors({
        target_end_date: ['تاريخ الانتهاء المستهدف يجب أن يكون بعد تاريخ البدء.'],
      });
      return;
    }
    setSubmitting(true);
    try {
      await createClientInitiative({
        input: {
          client_id: clientId,
          name: trimmed,
          description: description.trim() || null,
          start_date: startDate || null,
          target_end_date: targetEndDate || null,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{
        marginTop: 14,
        padding: 16,
        background: 'var(--cream-2)',
        border: '1px dashed var(--line-2)',
        borderRadius: 'var(--r-md)',
      }}
    >
      {error ? (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}
      <FormField
        id="initiative_name"
        label="اسم المبادرة"
        value={name}
        onChange={setName}
        required
        maxLength={160}
        disabled={submitting}
        placeholder="مثال: حملة إطلاق رمضان"
        inputRef={ref}
        error={fieldErrors['name']?.[0]}
      />
      <FormField
        id="initiative_description"
        label="الوصف (اختياري)"
        value={description}
        onChange={setDescription}
        maxLength={2000}
        disabled={submitting}
        textarea
        rows={3}
        error={fieldErrors['description']?.[0]}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
          gap: 14,
        }}
      >
        <FormField
          id="initiative_start_date"
          label="تاريخ البدء (اختياري)"
          value={startDate}
          onChange={setStartDate}
          disabled={submitting}
          type="date"
          dirLtr
          error={fieldErrors['start_date']?.[0]}
        />
        <FormField
          id="initiative_target_end_date"
          label="تاريخ الانتهاء المستهدف (اختياري)"
          value={targetEndDate}
          onChange={setTargetEndDate}
          disabled={submitting}
          type="date"
          dirLtr
          error={fieldErrors['target_end_date']?.[0]}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button type="submit" className="auth-cta" disabled={submitting} style={{ width: 'auto' }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الإضافة…' : 'إضافة المبادرة'}</span>
        </button>
        <button
          type="button"
          className="btn-text"
          onClick={onCancel}
          disabled={submitting}
          style={{ padding: '10px 14px' }}
        >
          إلغاء
        </button>
      </div>
    </form>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function handleApiErr(
  err: unknown,
  setError: (v: string | null) => void,
  setFieldErrors: (v: Record<string, string[]>) => void,
): void {
  if (err instanceof KmiApiError) {
    if (err.code === 'validation_error' && err.body.fields) {
      const next: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(err.body.fields)) next[k] = [...v];
      setFieldErrors(next);
      return;
    }
    if (err.code === 'permission_denied') {
      setError('لا تملك صلاحية تنفيذ هذه العملية.');
      return;
    }
    if (err.code === 'not_found') {
      setError('العميل غير متاح أو تمت أرشفته.');
      return;
    }
    if (err.code === 'conflict') {
      setError('هذا السجل موجود بالفعل.');
      return;
    }
    if (err.code === 'unauthenticated') {
      setError('انتهت الجلسة. سيتم إعادة تسجيل الدخول…');
      return;
    }
  }
  setError('تعذّر إكمال العملية. حاول مجدداً.');
}

function statusLabel(status: ClientStatus): string {
  switch (status) {
    case 'onboarding':
      return 'تأهيل';
    case 'active':
      return 'نشط';
    case 'paused':
      return 'متوقف';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

function initiativeLabel(status: InitiativeStatus): string {
  switch (status) {
    case 'planning':
      return 'تخطيط';
    case 'in_progress':
      return 'جاري';
    case 'at_risk':
      return 'في خطر';
    case 'completed':
      return 'مكتمل';
    case 'cancelled':
      return 'ملغى';
    default:
      return status;
  }
}

function initiativeVariant(status: InitiativeStatus): string {
  switch (status) {
    case 'planning':
      return 'draft';
    case 'in_progress':
      return 'active';
    case 'at_risk':
      return 'review';
    case 'completed':
      return 'info';
    case 'cancelled':
      return 'archived';
    default:
      return 'info';
  }
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}

interface FormFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly required?: boolean;
  readonly maxLength?: number;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly type?: 'text' | 'date';
  readonly dirLtr?: boolean;
  readonly textarea?: boolean;
  readonly rows?: number;
  readonly inputRef?: React.Ref<HTMLInputElement>;
  readonly error?: string;
}

function FormField(props: FormFieldProps): ReactNode {
  const errorId = props.error ? `${props.id}_error` : undefined;
  return (
    <div className="auth-field" style={{ marginBottom: 0 }}>
      <label className="auth-field-label" htmlFor={props.id}>
        {props.label}
        {props.required ? ' *' : ''}
      </label>
      {props.textarea ? (
        <textarea
          id={props.id}
          className="auth-input"
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          placeholder={props.placeholder}
          disabled={props.disabled}
          maxLength={props.maxLength}
          rows={props.rows ?? 2}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={errorId}
          style={{ paddingInlineStart: 18, resize: 'vertical' }}
        />
      ) : (
        <input
          ref={props.inputRef}
          id={props.id}
          className="auth-input"
          type={props.type ?? 'text'}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          placeholder={props.placeholder}
          disabled={props.disabled}
          maxLength={props.maxLength}
          dir={props.dirLtr ? 'ltr' : undefined}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={errorId}
          style={{ paddingInlineStart: 18 }}
        />
      )}
      {props.error ? (
        <div id={errorId} className="tiny" style={{ color: 'var(--rose)', marginTop: 4 }}>
          {props.error}
        </div>
      ) : null}
    </div>
  );
}
